import React, { useState } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  BarChart3,
  Users,
  Filter,
  Layers,
  CalendarClock,
  Globe,
  Shield,
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import OverviewTab from './components/tabs/OverviewTab'
import UserTrackingTab from './components/tabs/UserTrackingTab'
import FunnelsTab from './components/tabs/FunnelsTab'
import FeatureAdoptionTab from './components/tabs/FeatureAdoptionTab'
import RetentionTab from './components/tabs/RetentionTab'
import LandingPageTab from './components/tabs/LandingPageTab'
import SecurityTab from './components/tabs/SecurityTab'

const TAB_KEYS = [
  { key: 'overview', labelKey: 'analytics_tab_overview', icon: BarChart3 },
  { key: 'users', labelKey: 'analytics_tab_users', icon: Users },
  { key: 'funnels', labelKey: 'analytics_tab_funnels', icon: Filter },
  { key: 'features', labelKey: 'analytics_tab_features', icon: Layers },
  { key: 'retention', labelKey: 'analytics_tab_retention', icon: CalendarClock },
  { key: 'landing', labelKey: 'analytics_tab_landing', icon: Globe },
  { key: 'security', labelKey: 'analytics_tab_security', icon: Shield },
]

const PERIOD_KEYS = [
  { value: '7d', labelKey: 'analytics_period_7d' },
  { value: '30d', labelKey: 'analytics_period_30d' },
  { value: '90d', labelKey: 'analytics_period_90d' },
]

const AnalyticsScreen = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main
  const isDark = theme.palette.mode === 'dark'
  const navigate = useNavigate()
  const location = useLocation()

  // Derive active sub-tab from URL
  const segments = location.pathname.replace(/^\/admin\/analytics\/?/, '').split('/')
  const activeKey = TAB_KEYS.find((tab) => tab.key === segments[0])?.key || 'overview'
  const activeTabIndex = TAB_KEYS.findIndex((tab) => tab.key === activeKey)

  const [period, setPeriod] = useState('30d')

  const handleTabChange = (_, newIndex) => {
    const tab = TAB_KEYS[newIndex]
    if (tab) {
      navigate(`/admin/analytics/${tab.key}`)
    }
  }

  const renderTab = () => {
    switch (activeKey) {
      case 'overview': return <OverviewTab period={period} />
      case 'users': return <UserTrackingTab period={period} />
      case 'funnels': return <FunnelsTab period={period} />
      case 'features': return <FeatureAdoptionTab period={period} />
      case 'retention': return <RetentionTab period={period} />
      case 'landing': return <LandingPageTab period={period} />
      case 'security': return <SecurityTab period={period} />
      default: return <OverviewTab period={period} />
    }
  }

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 2 },
        height: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: alpha(goldColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: goldColor,
            }}
          >
            <BarChart3 size={20} strokeWidth={1.75} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.02em',
                color: theme.palette.text.primary,
              }}
            >
              {t('analytics_title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
              }}
            >
              {t('analytics_description')}
            </Typography>
          </Box>
        </Box>

        {/* Period selector */}
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, val) => val && setPeriod(val)}
          size="small"
          sx={{
            gap: 1,
            '& .MuiToggleButton-root': {
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              px: 2,
              py: 0.75,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                background: alpha(goldColor, isDark ? 0.12 : 0.08),
                color: goldColor,
                borderColor: alpha(goldColor, 0.3),
                '&:hover': {
                  background: alpha(goldColor, isDark ? 0.18 : 0.12),
                },
              },
              '&:hover': {
                background: alpha(theme.palette.text.primary, 0.04),
              },
            },
          }}
        >
          {PERIOD_KEYS.map(p => (
            <ToggleButton key={p.value} value={p.value}>
              {t(p.labelKey)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Tab navigation */}
      <Box
        sx={{
          mb: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.85rem',
              minHeight: 40,
              px: 2,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: goldColor,
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: goldColor,
              height: 2,
              borderRadius: '2px 2px 0 0',
            },
          }}
        >
          {TAB_KEYS.map((tab) => {
            const Icon = tab.icon
            return (
              <Tab
                key={tab.key}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Icon size={15} strokeWidth={1.75} />
                    {t(tab.labelKey)}
                  </Box>
                }
              />
            )
          })}
        </Tabs>
      </Box>

      {/* Tab content */}
      {renderTab()}
    </Box>
  )
}

export default AnalyticsScreen
